import { z } from "zod";

/**
 * # EntityColor — the typed per-entity colour model (EPIC-COLOR-UNION S1)
 *
 * AutoCAD entity colour (DXF group 62 / 420 / 430) is a *union* of distinct
 * cases, NOT a single scalar. Before this slice the model collapsed it to a
 * bare `color: string` ("ByLayer" | "0" | "5" | "#rrggbb"), which had no place
 * for true-colour (group 420) or color-book names (group 430) — both were
 * DROPPED on import. This typed union is the single CAD-core source of truth
 * (CLAUDE.md priority #1 — model the AutoCAD semantics fully, no boolean/string
 * flattening). It is projected to the legacy string for the renderer / property
 * bag via {@link colorToLegacyString} during the S1 migration.
 *
 * ## Verified group-code mapping (Autodesk DXF reference + ezdxf)
 *
 *  - **62** — ACI (AutoCAD Color Index) integer:
 *      - `0`   → BYBLOCK
 *      - `256` → BYLAYER
 *      - `1..255` → palette index
 *      - a *negative* value (entity-level) marks the entity's layer OFF; it is
 *        RARE but legal — we preserve the sign verbatim (`abs()` only for
 *        display, never for storage / round-trip). ezdxf likewise keeps the raw
 *        signed value.
 *  - **420** — 24-bit true colour `0x00RRGGBB` (R = bits 16-23, G = 8-15,
 *      B = 0-7). When present it OVERRIDES 62 for *display*, but a coexisting 62
 *      MUST still be preserved for round-trip (ezdxf keeps both: a true-colour
 *      entity can still carry an ACI). So `rgb` carries an optional `aci`.
 *  - **430** — colour-book name string, e.g. `"PANTONE+ SOLID COATED$PANTONE 7406 C"`
 *      (book "$" colour). Preserve VERBATIM — never parse, synthesize, or derive
 *      it from 420/62 (memory feedback-roundtrip-preserve-source-never-infer).
 *
 * Resolution precedence (for `colorFromDxf`): 420 → 430 → 62. A 420 present wins
 * the *kind* (`rgb`) while still folding any 62/430 in as `aci`/`name`; a 430
 * without 420 yields `bookColor`; otherwise 62 decides byBlock/byLayer/index.
 */
export type EntityColor =
  | { kind: "byLayer" }
  | { kind: "byBlock" }
  | { kind: "index"; aci: number }
  | { kind: "rgb"; r: number; g: number; b: number; aci?: number; name?: string }
  | { kind: "bookColor"; name: string; aci?: number };

/**
 * Zod discriminated union mirroring {@link EntityColor}. `aci` is an int
 * (negative allowed — layer-off entity colours), `r/g/b` are 0-255 ints.
 */
export const entityColorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("byLayer") }),
  z.object({ kind: z.literal("byBlock") }),
  z.object({ kind: z.literal("index"), aci: z.number().int() }),
  z.object({
    kind: z.literal("rgb"),
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
    aci: z.number().int().optional(),
    name: z.string().optional(),
  }),
  z.object({
    kind: z.literal("bookColor"),
    name: z.string(),
    aci: z.number().int().optional(),
  }),
]);

// ── int ↔ rgb codec ────────────────────────────────────────────────────────

/**
 * Decode a 24-bit `0x00RRGGBB` integer (DXF group 420 payload) into channels.
 * R = bits 16-23, G = 8-15, B = 0-7 (Autodesk DXF ref / ezdxf `int2rgb`).
 */
export function int2rgb(value: number): { r: number; g: number; b: number } {
  const v = value & 0xffffff;
  return {
    r: (v >> 16) & 0xff,
    g: (v >> 8) & 0xff,
    b: v & 0xff,
  };
}

/** Encode RGB channels into a 24-bit `0x00RRGGBB` integer (DXF group 420). */
export function rgb2int(rgb: { r: number; g: number; b: number }): number {
  return ((rgb.r & 0xff) << 16) | ((rgb.g & 0xff) << 8) | (rgb.b & 0xff);
}

// ── legacy-string codecs (migration bridge) ─────────────────────────────────

/** Two-digit lowercase hex for a 0-255 channel. */
function hex2(n: number): string {
  return (n & 0xff).toString(16).padStart(2, "0");
}

/**
 * Parse an AutoCAD "Truecolor (R,G,B)" decimal channel triple into an `rgb`
 * {@link EntityColor}, or `null` when the string is not a valid triple. Accepts
 * a leading `Truecolor` keyword (AutoCAD shortcut `T`; RU-layout `Ц`) separated
 * from the triple by whitespace — e.g. `"T 255,128,0"`, `"truecolor 0,0,0"`,
 * `"255,128,0"`. Whitespace around channels is tolerated (`"255, 128, 0"`).
 *
 * Each channel must be an INTEGER in 0-255; an out-of-range channel, a non-3
 * arity, or any trailing garbage returns `null` (caller falls back to the safe
 * default) — never clamp / round / truncate invented colour data. This is the
 * ONLY true-colour-triple parser (jscpd one-truth); CHPROP / AI / property-grid
 * all reach it through {@link coerceColor}. Also EXPORTED so the strict COLOR-
 * command validator (`apps/web/src/app/dynamicInput/colorCommand.ts`
 * `parseColorCommandValue`) reuses the SAME triple validation instead of
 * duplicating the channel-range regex (jscpd one-truth, CLAUDE.md priority #1).
 */
export function parseTruecolorTriple(raw: string): EntityColor | null {
  // Strip an optional leading Truecolor keyword (`T` / `Truecolor` / `Ц`)
  // followed by whitespace. (A `\b` word-boundary cannot be used: in JS the
  // ASCII `\b` does not fire after the Cyrillic `Ц`. Requiring trailing
  // whitespace `\s+` both delimits the keyword and rules out matching a leading
  // `t` of a longer token like `teal`, which has no space after the `t`.)
  const body = raw
    .replace(/^(?:truecolor|t|ц)\s+/i, "")
    .trim();
  const m = /^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/.exec(body);
  if (!m) return null;
  const r = Number.parseInt(m[1]!, 10);
  const g = Number.parseInt(m[2]!, 10);
  const b = Number.parseInt(m[3]!, 10);
  if (r > 255 || g > 255 || b > 255) return null;
  return { kind: "rgb", r, g, b };
}

/**
 * STRICT validation of a colour-COMMAND / AI-action VALUE — the one-truth
 * counterpart to the PERMISSIVE {@link coerceColor} migration codec. Where
 * `coerceColor` maps an unknown string → ByLayer and an arbitrary int → an
 * out-of-range `index` (acceptable for legacy DXF load, which may legitimately
 * carry a negative layer-off ACI), this validator accepts EXACTLY the AutoCAD
 * COLOR-prompt forms and returns `null` for everything else, so a live user /
 * AI input cannot silently reset to ByLayer or store a `62=999` the export
 * would emit invalid (AutoCAD answers `"Invalid color."` and re-prompts):
 *
 *   - `ByLayer` / `256` / `""`     → byLayer
 *   - `ByBlock` / `0`              → byBlock
 *   - ACI index `1..255`           → index (verbatim — NO negative, NO 0/256
 *                                    as index, NO >255)
 *   - `#rrggbb` / bare `rrggbb`    → rgb (hex — reuses the {@link int2rgb} codec)
 *   - `R,G,B` truecolor (each      → rgb (reuses the ONE-truth
 *     0..255, optional leading       {@link parseTruecolorTriple})
 *     `Truecolor`/`T`/`Ц` keyword)
 *
 * This is the SINGLE strict colour-VALUE validator (jscpd one-truth, CLAUDE.md
 * priority #1): BOTH the user COLOR command
 * (`apps/web/src/app/dynamicInput/colorCommand.ts` `parseColorCommandValue`)
 * AND the AI `SET_CURRENT_COLOR` translator (`packages/ai-commands`
 * `translateSetCurrentColor`) reach it, so the human and AI seams enforce the
 * identical accept-list — the AI (a less-trusted contour per CLAUDE.md) cannot
 * bypass the gate the human path enforces. `coerceColor` stays for the load /
 * DXF-round-trip / property-bag migration path.
 */
export function parseColorCommandValueStrict(raw: string): EntityColor | null {
  const trimmed = raw.trim();
  if (trimmed === "") return { kind: "byLayer" };

  const lower = trimmed.toLowerCase();
  // English + numeric forms, PLUS the localized RU keywords the UI advertises
  // (`colorcmd.bylayer` = "ПОСЛОЮ", `colorcmd.byblock` = "ПОБЛОКУ"). A RU-session
  // user typing the echoed prompt default (`ЦВЕТ ПОСЛОЮ` / a `-COLOR` reply of
  // ПОСЛОЮ) must be ACCEPTED, not rejected. The RU tokens are ADDED valid forms
  // (case-insensitive — `.toLowerCase()` case-folds these Cyrillic letters
  // uniformly: ПОСЛОЮ→послою, ПОБЛОКУ→поблоку); the strict-reject contract for
  // unknown keywords / out-of-range ACI is unchanged. Lives in the SHARED parser
  // so CLI + batch + AI inherit it (jscpd one-truth, CLAUDE.md priority #1).
  if (lower === "bylayer" || lower === "послою" || trimmed === "256") {
    return { kind: "byLayer" };
  }
  if (lower === "byblock" || lower === "поблоку" || trimmed === "0") {
    return { kind: "byBlock" };
  }

  // Hex (#rrggbb or bare rrggbb) — the ONLY hex shape allowed (6 hex digits).
  const hexBody = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (/^[0-9a-f]{6}$/i.test(hexBody)) {
    const n = Number.parseInt(hexBody, 16);
    return { kind: "rgb", ...int2rgb(n) };
  }

  // Truecolor R,G,B triple (one-truth validator — each channel 0..255 or null).
  const trueColor = parseTruecolorTriple(trimmed);
  if (trueColor) return trueColor;

  // Plain ACI index — STRICT 1..255 (0/256 handled above as byBlock/byLayer; a
  // negative or out-of-range value is REJECTED, unlike coerceColor which would
  // store it verbatim — AutoCAD's COLOR command never accepts those).
  if (/^\d+$/.test(trimmed)) {
    const aci = Number.parseInt(trimmed, 10);
    if (aci >= 1 && aci <= 255) return { kind: "index", aci };
    return null; // out of the 1..255 ACI range
  }

  // Anything else — invalid (unknown keyword / malformed hex / non-numeric).
  return null;
}

/**
 * Coerce ANY input into an {@link EntityColor}. **Idempotent** — an input that
 * is already a valid EntityColor object passes through UNCHANGED. This is
 * critical because the schema is re-parsed many times (every
 * `drawingDocumentSchema.parse` / `assembleDrawingDocument` in DEV) and the web
 * property write-back coerces the same value repeatedly; a non-idempotent
 * coercion would corrupt an already-typed colour.
 *
 * Legacy-string forms (the pre-S1 `color: string` representation):
 *   - `"ByLayer"` / `""` / `"256"`         → byLayer
 *   - `"ByBlock"` / `"0"`                    → byBlock
 *   - `"1".."255"` (and negative ints)       → index (verbatim aci)
 *   - `"#rrggbb"` / `"rrggbb"`               → rgb
 *   - `"R,G,B"` (0-255 each, optional        → rgb (S3b — AutoCAD "Truecolor
 *     leading `T`/`Truecolor`/`Ц` keyword)          (R,G,B)" VALUE form)
 *   - anything unrecognised                  → byLayer (safe default)
 *
 * ## S3b — AutoCAD "Truecolor (R,G,B)" colour-VALUE syntax (the ONE-truth site)
 *
 * AutoCAD's colour prompts accept *"… or Truecolor (R,G,B)"* — a decimal channel
 * triple (`255,128,0`), optionally led by the `Truecolor` keyword (shortcut `T`;
 * `Ц` for the RU keyboard layout). Parsing it HERE — the one colour-coerce site —
 * lights up every colour-VALUE input that already routes through this codec at
 * once: the CHPROP command-line value (`CHPROP Color=255,128,0`), the property-
 * grid picker, and the AI safe-contour, plus MATCHPROP (CLAUDE.md priority #1
 * AutoCAD parity; jscpd one-truth: no per-surface colour parser).
 *
 * NOTE: the standalone `-COLOR` / `COLOR` / `SETCOLOR` / `ЦВЕТ` COMMAND (which
 * sets CECOLOR, the current default colour for newly-created entities) IS wired
 * by the CECOLOR slice — `apps/web/src/app/dynamicInput/colorCommand.ts` reaches
 * this codec through {@link coerceColor} for the colour VALUE form, and
 * {@link cecolorFromHeader} / {@link cecolorToHeader} round-trip the `$CECOLOR`
 * HEADER var. This codec governs how a colour VALUE string is interpreted once a
 * colour input has been identified; the command itself lives in the web app.
 *
 * Each channel must be an integer 0-255; an
 * out-of-range channel or a 4-tuple falls through to the safe default rather than
 * silently clamping (never invent colour data — feedback-roundtrip-preserve-
 * source-never-infer). The triple maps to DXF group 420 exactly as `rgb2int`
 * encodes it, matching the `#rrggbb` path.
 */
export function coerceColor(input: unknown): EntityColor {
  // Idempotent fast-path — already a typed EntityColor object.
  if (input !== null && typeof input === "object" && "kind" in input) {
    const parsed = entityColorSchema.safeParse(input);
    if (parsed.success) return parsed.data;
    // Malformed object → fall through to the safe default below.
    return { kind: "byLayer" };
  }

  if (typeof input !== "string") return { kind: "byLayer" };

  const raw = input.trim();
  if (raw === "") return { kind: "byLayer" };

  const lower = raw.toLowerCase();
  if (lower === "bylayer" || raw === "256") return { kind: "byLayer" };
  if (lower === "byblock" || raw === "0") return { kind: "byBlock" };

  // Hex (#rrggbb or bare rrggbb).
  const hexBody = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-f]{6}$/i.test(hexBody)) {
    const n = Number.parseInt(hexBody, 16);
    return { kind: "rgb", ...int2rgb(n) };
  }

  // S3b — AutoCAD "Truecolor (R,G,B)" decimal triple (`255,128,0`), optionally
  // led by the `Truecolor` keyword (shortcut `T`, RU `Ц`). Tried BEFORE the
  // integer-index branch — a comma-separated triple can never match the bare
  // `-?\d+` index regex, so there is no collision, but keeping it here mirrors
  // AutoCAD's prompt order (true-colour offered alongside the index).
  const trueColor = parseTruecolorTriple(raw);
  if (trueColor) return trueColor;

  // Integer ACI index (incl. negative layer-off codes). `0`/`256` already
  // handled above as byBlock/byLayer; anything else is an index.
  if (/^-?\d+$/.test(raw)) {
    return { kind: "index", aci: Number.parseInt(raw, 10) };
  }

  // Unknown string — safe default (never throw on legacy data).
  return { kind: "byLayer" };
}

/**
 * Project an {@link EntityColor} to the pre-S1 legacy `color` string consumed
 * by the renderer colour resolver (`colorResolution.ts`) and the
 * `EntityPreview.properties.Color` bag during the S1 migration. The mapping is
 * EXACTLY the inverse of the legacy strings `coerceColor` accepts, so
 * ByLayer / ByBlock / index render byte-identically to before this slice:
 *   - byLayer   → "ByLayer"
 *   - byBlock   → "0"        (the renderer treats "0" as BYBLOCK)
 *   - index     → String(aci)
 *   - rgb       → "#rrggbb"  (lowercase — the renderer's #hex path renders it;
 *                             this is the S1 true-colour FIX)
 *   - bookColor → the source ACI (group 62) FALLBACK, projected like a bare 62
 *                 (0→"0" BYBLOCK, 256/absent→"ByLayer", else String(aci)). The
 *                 legacy string has no colour-book repr (the 430 swatch lands in
 *                 S2's union-aware resolver + picker), so the render/property/PDF
 *                 paths fall back to the ACI the file shipped alongside the 430 —
 *                 matching pre-S1, where a 430+62 entity rendered as String(62).
 */
export function colorToLegacyString(c: EntityColor): string {
  switch (c.kind) {
    case "byLayer":
      return "ByLayer";
    case "byBlock":
      return "0";
    case "index":
      return String(c.aci);
    case "rgb":
      return `#${hex2(c.r)}${hex2(c.g)}${hex2(c.b)}`;
    case "bookColor":
      // No legacy book-colour repr → fall back to the coexisting ACI (group 62),
      // projected exactly like a bare 62 so 430+62 renders its ACI fallback as
      // it did pre-S1. Only a bookColor with no fallback ACI (or 256) → ByLayer.
      if (c.aci === undefined || c.aci === 256) return "ByLayer";
      if (c.aci === 0) return "0";
      return String(c.aci);
  }
}

// ── semantic colour equality (S15 colour-edit decision procedure) ────────────

/**
 * TRUE iff `target` denotes the SAME colour as `current` — semantic identity of
 * the DISPLAYED colour, distinct from structural identity of the union value.
 *
 * The distinction exists because an {@link EntityColor} can carry ROUND-TRIP
 * SIBLINGS that are not part of the denoted colour: per the module header
 * (verified against the Autodesk DXF reference + ezdxf), a group 420 true colour
 * OVERRIDES a coexisting 62 (and 430) for display — the `aci`/`name` fields on
 * the `rgb` case are preserved source metadata, NOT the colour itself. So a
 * PLAIN `rgb` target (no siblings — the shape EVERY lossy string input produces:
 * `#FF8000`, `ff8000`, `255,128,0`, `T 255,128,0`) denotes the same colour as a
 * stored `rgb` carrying siblings with the same channels.
 *
 * Sibling rule is DIRECTIONAL (target-asserts): a sibling ABSENT on the target
 * asserts nothing (ignored — the stored richer form should be preserved on
 * no-op); a sibling PRESENT on the target is a deliberate lossless assertion
 * (only the `!EC:` wire form can carry one) and must MATCH — a lossless write
 * that changes only the 62/430 sibling is a REAL edit (round-trip bytes change),
 * never a no-op.
 *
 * `bookColor` compares EXACTLY (name AND fallback aci): no lossy string form can
 * denote a bookColor (only the `!EC:` payload), so a bookColor target is always
 * a deliberate lossless write, and the 430-name + 62-fallback pair must round-
 * trip verbatim (feedback-roundtrip-preserve-source-never-infer). A `bookColor`
 * is NEVER semantically equal to a plain `index`, even with an equal fallback
 * ACI — group 430 and group 62 are different colours (the book name wins).
 */
export function semanticColorEqual(current: EntityColor, target: EntityColor): boolean {
  switch (target.kind) {
    case "byLayer":
      return current.kind === "byLayer";
    case "byBlock":
      return current.kind === "byBlock";
    case "index":
      return current.kind === "index" && current.aci === target.aci;
    case "rgb":
      return (
        current.kind === "rgb" &&
        current.r === target.r &&
        current.g === target.g &&
        current.b === target.b &&
        (target.aci === undefined || target.aci === current.aci) &&
        (target.name === undefined || target.name === current.name)
      );
    case "bookColor":
      return (
        current.kind === "bookColor" &&
        current.name === target.name &&
        current.aci === target.aci
      );
  }
}

// ── lossless intern-string codec (compact-store round-trip) ─────────────────

/**
 * Sentinel prefix for the rich (non-legacy-representable) serialized form. No
 * legacy colour string can start with it ("ByLayer"/"ByBlock"/decimal int/
 * "#hex"/bare 6-hex), so {@link deserializeColor} can disambiguate the rich form
 * from a plain legacy string without collision.
 */
const RICH_COLOR_PREFIX = "!EC:";

/**
 * True when an {@link EntityColor} is FULLY represented by its legacy string —
 * i.e. {@link colorToLegacyString} → {@link coerceColor} is lossless for it.
 * Lossless: byLayer / byBlock / index / `rgb` with NO coexisting aci/name.
 * Lossy (needs the rich form): `rgb` carrying an aci/name, and every bookColor
 * (the legacy string collapses it to "ByLayer", dropping the 430 name + 62).
 */
function isLegacyLossless(c: EntityColor): boolean {
  switch (c.kind) {
    case "byLayer":
    case "byBlock":
    case "index":
      return true;
    case "rgb":
      return c.aci === undefined && c.name === undefined;
    case "bookColor":
      return false;
  }
}

/**
 * Serialize an {@link EntityColor} to a SINGLE string for interning in the
 * compact entity store (`compactEntityStore` / `compactStoreBase` Uint16 string
 * table). LOSSLESS for the full union — unlike {@link colorToLegacyString},
 * which drops the 62/430 siblings of true-/book-colours.
 *
 * For the legacy-representable cases (byLayer / byBlock / index / plain rgb) it
 * returns EXACTLY {@link colorToLegacyString}'s output, so the interned string
 * table stays BYTE-IDENTICAL for the common colours (Category-B no-regression).
 * The richer cases (rgb+aci/name, bookColor) get a `!EC:`-prefixed JSON
 * payload that {@link deserializeColor} restores verbatim.
 */
export function serializeColor(c: EntityColor): string {
  if (isLegacyLossless(c)) return colorToLegacyString(c);
  return RICH_COLOR_PREFIX + JSON.stringify(c);
}

/**
 * Inverse of {@link serializeColor}. A `!EC:`-prefixed payload is parsed +
 * validated back to the full union; any other string is a legacy colour string
 * routed through {@link coerceColor} (idempotent — also accepts an already-typed
 * object, defending raw entity literals that bypassed the schema).
 */
export function deserializeColor(input: unknown): EntityColor {
  if (typeof input === "string" && input.startsWith(RICH_COLOR_PREFIX)) {
    try {
      const parsed: unknown = JSON.parse(input.slice(RICH_COLOR_PREFIX.length));
      const result = entityColorSchema.safeParse(parsed);
      if (result.success) return result.data;
    } catch {
      // Malformed payload → fall through to the safe default.
    }
    return { kind: "byLayer" };
  }
  return coerceColor(input);
}

// ── DXF group-code codecs ────────────────────────────────────────────────────

/** The DXF common-colour group codes consumed/produced by the bridge. */
export interface DxfColorGroups {
  /** Group 62 — ACI index (0 BYBLOCK / 256 BYLAYER / 1-255 / negative). */
  colorNumber?: number;
  /** Group 420 — 24-bit `0x00RRGGBB` true colour. */
  trueColor?: number;
  /** Group 430 — colour-book name. */
  colorName?: string;
}

/**
 * Build an {@link EntityColor} from the parsed DXF common group codes
 * (resolution precedence 420 → 430 → 62 per the module header). A coexisting 62
 * — INCLUDING the symbolic `0` (BYBLOCK) and `256` (BYLAYER) — is folded into
 * the `rgb`/`bookColor` case so it round-trips VERBATIM. The writer omits a
 * STANDALONE `256` (BYLAYER is the implicit default), but emits it when it is an
 * explicit sibling of a 420/430 group (see `writer.ts` group-62 emitter), so
 * dropping a source-supplied `62=256` (or `62=0`) alongside a 420/430 would diff
 * the file. (memory feedback-roundtrip-preserve-source-never-infer — preserve
 * source group codes, do not infer "256/0 is just the default" and discard it.)
 * A genuinely ABSENT 62 stays absent (`aciOverride` undefined → no `aci`), so
 * the common 420-only / 430-only case carries no spurious code.
 */
export function colorFromDxf(c: DxfColorGroups): EntityColor {
  const aci = c.colorNumber;
  // Preserve a coexisting 62 VERBATIM (incl. symbolic `0`/BYBLOCK, `256`/BYLAYER
  // and negative layer-off codes) when a 420/430 sibling decides the `kind`.
  // Only a genuinely absent 62 yields no `aci`. The writer emits the 62 only in
  // this coexisting form (a standalone 256 is still omitted there), so carrying
  // it preserves a real source group rather than inventing one.
  const aciOverride = aci;

  if (c.trueColor !== undefined) {
    const rgb = int2rgb(c.trueColor);
    return {
      kind: "rgb",
      ...rgb,
      ...(aciOverride !== undefined ? { aci: aciOverride } : {}),
      ...(c.colorName ? { name: c.colorName } : {}),
    };
  }

  if (c.colorName) {
    return {
      kind: "bookColor",
      name: c.colorName,
      ...(aciOverride !== undefined ? { aci: aciOverride } : {}),
    };
  }

  if (aci === undefined || aci === 256) return { kind: "byLayer" };
  if (aci === 0) return { kind: "byBlock" };
  // 1-255 and negative (layer-off) — verbatim index.
  return { kind: "index", aci };
}

/**
 * Project an {@link EntityColor} back to the DXF common group codes. Mirrors
 * `colorFromDxf` and the dxf-io writer contract:
 *   - byLayer   → {}                     (NO group 62 — the writer omits BYLAYER,
 *                                          keeping output byte-identical; it does
 *                                          NOT emit 256)
 *   - byBlock   → { colorNumber: 0 }
 *   - index     → { colorNumber: aci }   (verbatim, incl. negative)
 *   - rgb       → { trueColor, +colorNumber: aci?, +colorName: name? }
 *   - bookColor → { colorName, +colorNumber: aci? }
 */
export function colorToDxf(c: EntityColor): DxfColorGroups {
  switch (c.kind) {
    case "byLayer":
      return {};
    case "byBlock":
      return { colorNumber: 0 };
    case "index":
      return { colorNumber: c.aci };
    case "rgb":
      return {
        trueColor: rgb2int(c),
        ...(c.aci !== undefined ? { colorNumber: c.aci } : {}),
        ...(c.name !== undefined ? { colorName: c.name } : {}),
      };
    case "bookColor":
      return {
        colorName: c.name,
        ...(c.aci !== undefined ? { colorNumber: c.aci } : {}),
      };
  }
}

// ── $CECOLOR HEADER-scalar codec (current-entity-colour sysvar) ──────────────

/**
 * AcCmEntityColor raw-color "type byte" constants (ezdxf `colors.py`, verified).
 * The current-entity-colour HEADER var `$CECOLOR` (group 62) is a SINGLE signed
 * int — unlike the per-entity 62/420/430 group SET that {@link colorFromDxf}
 * consumes. AutoCAD encodes ByLayer / ByBlock / true-colour into that one int
 * via the raw-color form: the high byte (bits 24-31) is a TYPE tag, the low 24
 * bits the payload. A plain positive 1..255 stays a bare ACI index; a negative
 * value is the raw-color form (its sign comes from the type byte's top bit).
 */
const COLOR_TYPE_BY_LAYER = 0xc0;
const COLOR_TYPE_BY_BLOCK = 0xc1;
const COLOR_TYPE_RGB = 0xc2;
const COLOR_TYPE_ACI = 0xc3;

/**
 * Decode the DXF HEADER `$CECOLOR` scalar (group 62, one signed int) into an
 * {@link EntityColor}. This is the current-entity-colour system variable — the
 * colour AutoCAD assigns to NEWLY-created entities. It is DISTINCT from the
 * per-entity 62/420/430 group set ({@link colorFromDxf}): `$CECOLOR` packs the
 * whole union into ONE int via the AcCmEntityColor raw-color encoding.
 *
 * Mapping (Autodesk DXF reference `$CECOLOR` group 62 default 256; ezdxf
 * `colors.py` raw-color constants):
 *   - `undefined` / `256` → ByLayer (the default — the writer OMITS it)
 *   - `0`                 → ByBlock
 *   - `1..255`            → plain ACI index
 *   - negative (raw-color form) — classify by the type byte (bits 24-31):
 *       `0xC0` → ByLayer, `0xC1` → ByBlock,
 *       `0xC2` → true colour (`rgb` from the low 24 bits),
 *       `0xC3` → ACI (low byte)
 *   - any OTHER negative (e.g. an entity-level layer-off code that should never
 *     appear in the HEADER) → preserved VERBATIM as an `index` (never invent /
 *     discard source data — feedback-roundtrip-preserve-source-never-infer).
 */
export function cecolorFromHeader(n: number | undefined): EntityColor {
  if (n === undefined || n === 256) return { kind: "byLayer" };
  if (n === 0) return { kind: "byBlock" };
  if (n > 0 && n < 256) return { kind: "index", aci: n };
  // Negative → raw AcCmColor form; classify by the type byte.
  const u = n >>> 0;
  const type = (u >>> 24) & 0xff;
  if (type === COLOR_TYPE_BY_LAYER) return { kind: "byLayer" };
  if (type === COLOR_TYPE_BY_BLOCK) return { kind: "byBlock" };
  if (type === COLOR_TYPE_RGB) return { kind: "rgb", ...int2rgb(u & 0xffffff) };
  if (type === COLOR_TYPE_ACI) return { kind: "index", aci: u & 0xff };
  // Unknown negative form → preserve verbatim as an index (no inference).
  return { kind: "index", aci: n };
}

/**
 * Project an {@link EntityColor} back to the DXF HEADER `$CECOLOR` scalar (group
 * 62). Inverse of {@link cecolorFromHeader}; the writer omits a standalone 256
 * (ByLayer is the implicit default — `headerDocumentVars.ts` spec
 * `defaultValue: 256`), keeping a ByLayer-current-colour drawing byte-identical.
 *
 *   - byLayer   → 256  (writer omits)
 *   - byBlock   → 0
 *   - index     → aci  (plain, incl. a verbatim negative round-tripped above)
 *   - rgb       → the negative AcCmEntityColor raw value `0xC2RRGGBB` re-
 *                 interpreted as a signed 32-bit int. TRUE COLORS ARE VALID
 *                 `$CECOLOR` values — the Autodesk CECOLOR sysvar doc (GUID-
 *                 703858E3-9038-406A-B499-90A0C05BECBF) lists "True Colors: RGB
 *                 or HSL" among the valid CECOLOR values *Saved in: Drawing*, so a
 *                 `COLOR #ff8000` current colour MUST survive save → reopen, not
 *                 reset to ByLayer. AutoCAD stores it as the raw AcCmEntityColor
 *                 (type byte `0xC2` = RGB, low 24 bits the colour) packed into the
 *                 group-62 int; sign-reinterpreted it reads as a negative integer.
 *                 This matches ezdxf `colors.py` `encode_raw_color(rgb)` =
 *                 `-(-(0xC2000000 + rgb2int) & 0xFFFFFFFF)`. The dxf-io transport
 *                 (`headerDocumentVars.ts` `$CECOLOR` group 62 `int:true`) already
 *                 round-trips negative C2 values verbatim, so this needs no writer
 *                 change. {@link cecolorFromHeader} decodes the same C2 negative
 *                 back to rgb — the pair round-trips identity.
 *   - bookColor → the coexisting ACI fallback (the HEADER `$CECOLOR` scalar is a
 *                 single group-62 int with NO 430 colour-book-name companion slot
 *                 — the 430 slot exists only in the per-ENTITY 62/420/430 group
 *                 SET, not for the header current-colour). AutoCAD has no header
 *                 representation for a book-name current colour, so we fall back to
 *                 the book's stored ACI, mirroring {@link colorToLegacyString}'s
 *                 bookColor path; absent/256 → 256 (ByLayer). This is NOT a
 *                 reversal — bookColor (only) lacks a header repr.
 */
export function cecolorToHeader(c: EntityColor): number {
  switch (c.kind) {
    case "byLayer":
      return 256;
    case "byBlock":
      return 0;
    case "index":
      return c.aci;
    case "rgb":
      // True colour → the AcCmEntityColor raw value (type byte 0xC2 = RGB | the
      // packed 24-bit colour), sign-reinterpreted to a negative signed int via
      // `| 0`. Matches ezdxf `encode_raw_color`; round-trips with the C2 decode
      // in `cecolorFromHeader`. True colours ARE valid $CECOLOR (Autodesk doc).
      return (COLOR_TYPE_RGB * 0x1000000 + rgb2int(c)) | 0;
    case "bookColor":
      return c.aci ?? 256;
  }
}
